import type { SichererSpeicher } from './sicherer-speicher';
import { TresorUnlesbar } from './sicherer-speicher';

/**
 * Gerätezugang nach der Aktivierung V2 und Wahl der Einrichtung beim Start.
 *
 * Reihenfolge beim Start (automatisches Wiederanmelden nach Neustart):
 * 1. Zugang V2 im sicheren Speicher → Kasse mit diesem Zugang.
 * 2. Sonst bisherige Einrichtung (Kassen-ID + Kassenschlüssel in localStorage) → unverändert
 *    weiter (Fallback, wird nie automatisch gelöscht oder ersetzt).
 * 3. Sonst Willkommen/Aktivierung.
 *
 * Die Bedieneranmeldung (PIN, `bediener.ts`) ist davon getrennt und folgt danach wie bisher.
 */
export const ZUGANG_EINTRAG = 'aktivierung.zugang';
export const SITZUNG_EINTRAG = 'aktivierung.sitzung';

/** Bisherige Schlüssel (siehe `../aktivierung.ts`, `App.tsx`). */
export const ALT_KASSEN_ID = 'bilanx.kassenId';
export const ALT_KASSENSCHLUESSEL = 'bilanx.kassenschluessel';

export interface GeraeteZugang {
  kassenId: string;
  geraetId: string;
  /** Nie anzeigen, nie loggen. */
  nachweis: string;
  kasseName: string;
  unternehmerName: string;
  /** ms seit Epoche */
  aktiviertAm: number;
}

function istZugang(x: unknown): x is GeraeteZugang {
  if (typeof x !== 'object' || x === null) {
    return false;
  }
  const z = x as Record<string, unknown>;
  return (
    typeof z.kassenId === 'string' &&
    z.kassenId !== '' &&
    typeof z.geraetId === 'string' &&
    typeof z.nachweis === 'string' &&
    z.nachweis !== '' &&
    typeof z.kasseName === 'string' &&
    typeof z.unternehmerName === 'string' &&
    typeof z.aktiviertAm === 'number'
  );
}

/** Zugang lesen; null wenn keiner gespeichert ist. Unlesbar/kaputt → `TresorUnlesbar`. */
export async function ladeZugang(speicher: SichererSpeicher): Promise<GeraeteZugang | null> {
  const roh = await speicher.lese(ZUGANG_EINTRAG);
  if (roh === null) {
    return null;
  }
  let wert: unknown;
  try {
    wert = JSON.parse(roh);
  } catch {
    throw new TresorUnlesbar(ZUGANG_EINTRAG);
  }
  if (!istZugang(wert)) {
    throw new TresorUnlesbar(ZUGANG_EINTRAG);
  }
  return wert;
}

export async function speichereZugang(speicher: SichererSpeicher, z: GeraeteZugang): Promise<void> {
  await speicher.schreibe(ZUGANG_EINTRAG, JSON.stringify(z));
  // Zurücklesen: erst ein lesbarer Zugang gilt als gespeichert
  const kontrolle = await ladeZugang(speicher);
  if (kontrolle?.nachweis !== z.nachweis) {
    throw new TresorUnlesbar(ZUGANG_EINTRAG);
  }
}

export type Einrichtung =
  | { art: 'v2'; zugang: GeraeteZugang }
  | { art: 'alt'; kassenId: string; schluessel: string }
  /** V2-Zugang vorhanden, aber nicht lesbar und keine bisherige Einrichtung. */
  | { art: 'unlesbar'; meldung: string }
  | { art: 'keine'; sicherMoeglich: boolean };

interface Ablage {
  getItem(schluessel: string): string | null;
}

export async function ermittleEinrichtung(
  speicher: SichererSpeicher | null,
  ablage: Ablage,
): Promise<Einrichtung> {
  let unlesbar: string | null = null;
  if (speicher !== null) {
    try {
      const z = await ladeZugang(speicher);
      if (z !== null) {
        return { art: 'v2', zugang: z };
      }
    } catch (e) {
      unlesbar = e instanceof Error ? e.message : 'Gerätezugang nicht lesbar';
    }
  }
  const kassenId = ablage.getItem(ALT_KASSEN_ID) ?? '';
  const schluessel = ablage.getItem(ALT_KASSENSCHLUESSEL) ?? '';
  if (kassenId !== '' && schluessel !== '') {
    return { art: 'alt', kassenId, schluessel };
  }
  if (unlesbar !== null) {
    return { art: 'unlesbar', meldung: unlesbar };
  }
  return { art: 'keine', sicherMoeglich: speicher !== null };
}

/**
 * Liefert Kassen-ID und Token synchron für `KassenApi` (Option `token`). V2-Zugang hat Vorrang;
 * ohne ihn gilt die bisherige Einrichtung aus localStorage – so bleibt die alte Einrichtung
 * („Kassenschlüssel eintragen“) jederzeit funktionsfähig.
 */
export class ZugangsHalter {
  private zugang: GeraeteZugang | null;

  constructor(
    private readonly ablage: Ablage,
    einrichtung: Einrichtung,
  ) {
    this.zugang = einrichtung.art === 'v2' ? einrichtung.zugang : null;
  }

  token(): string | null {
    if (this.zugang !== null) {
      return this.zugang.nachweis;
    }
    const s = this.ablage.getItem(ALT_KASSENSCHLUESSEL);
    return s === null || s === '' ? null : s;
  }

  kassenId(): string {
    return this.zugang?.kassenId ?? this.ablage.getItem(ALT_KASSEN_ID) ?? '';
  }

  istV2(): boolean {
    return this.zugang !== null;
  }

  /** Nach erfolgreicher Aktivierung. */
  setze(z: GeraeteZugang | null): void {
    this.zugang = z;
  }
}
